import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { graphql, useStaticQuery } from 'gatsby';
import Img from 'gatsby-image';
import Layout from '../components/layout';
import SEO from '../components/seo';
import { BREAKPOINTS, COLORS } from '../ui/variables';
import Heading1 from '../ui/Heading1';
import ParagraphExLarge from '../ui/ParagraphExLarge';
import { TitleMedium, Uppercase } from '../ui/TypographyStyles';
import ParagraphLarge from '../ui/ParagraphLarge';

const Header = styled.div`
  background-color: ${COLORS.NEW_BEIGE};
`;

const MaxContent = styled.div`
  margin: auto;
  max-width: 1246px;
`;

const HeadContent = styled(MaxContent)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 60px 50px;

  @media screen and (max-width: 700px) {
    flex-direction: column;
    padding: 60px 20px;
  }
`;

const OurStory = styled(MaxContent)`
  padding: 100px 50px;

  @media ${BREAKPOINTS.MOBILE} {
    padding: 50px 20px;
  }
`;

const OurResponsibilityWrapper = styled.div`
  background-color: ${COLORS.NEW_BEIGE};
`;

const OurResponsibility = styled(MaxContent)`
  padding: 100px 50px;

  @media ${BREAKPOINTS.MOBILE} {
    padding: 50px 20px;
  }
`;

const PeaceOfMind = styled(MaxContent)`
  padding: 100px 50px;

  @media ${BREAKPOINTS.MOBILE} {
    padding: 50px 20px;
  }
`;

const POMPoints = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  list-style: none;
  margin: 0;

  strong {
    font-weight: 600;
  }
`;

const POMPoint = styled(ParagraphExLarge)`
  width: 48%;

  @media ${BREAKPOINTS.MOBILE} {
    width: 100%;
  }
`;

const AboutUsPage = () => {
  const data = useStaticQuery(graphql`
    query {
      headerImage: file(relativePath: { eq: "photos/dog-guy-blue-sky.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 450) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      highFive: file(relativePath: { eq: "graphics/high-five.png" }) {
        childImageSharp {
          fluid(maxWidth: 200) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      dogInWater: file(
        relativePath: { eq: "pages/about-us/dog-in-water.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 200) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      ladyLayingWithDog: file(
        relativePath: { eq: "pages/about-us/lady-dog-laying.jpg" }
      ) {
        childImageSharp {
          fluid(maxWidth: 1800) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      feetPaws: file(
        relativePath: { eq: "pages/about-us/dog-paws-people-feet.jpg" }
      ) {
        childImageSharp {
          fluid(maxWidth: 400) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  `);

  return (
    <Layout>
      <SEO
        title='About Us | Our Story &amp; Values'
        description="Learn about us, our founders' vision and what inspired Small Door Veterinary to radically reimagine the healthcare experience for modern pet owners."
      />
      <Header>
        <HeadContent>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              width: 50%;
              @media screen and (max-width: 700px) {
                width: 100%;
              }
            `}
          >
            <Heading1>About Small Door Veterinary</Heading1>
            <ParagraphExLarge>
              It's not always easy to find convenient and reliable veterinary
              care, or even to know what your pet needs. We're changing all of
              that.
            </ParagraphExLarge>
            <Img
              css={css`
                max-width: 260px;
                width: 100%;
                align-self: flex-end;
                @media screen and (max-width: 700px) {
                  display: none;
                }
              `}
              fluid={data.highFive.childImageSharp.fluid}
              alt='Sketch of woman and dog high fiving'
            />
          </div>
          <div
            css={css`
              width: 40%;
              max-width: 480px;
              @media screen and (max-width: 700px) {
                width: 100%;
                order: -1;
                margin-bottom: 40px;
              }
            `}
          >
            <Img
              fluid={data.headerImage.childImageSharp.fluid}
              alt='Man sitting with dog'
            />
          </div>
        </HeadContent>
      </Header>

      <OurStory>
        <Uppercase
          css={css`
            color: ${COLORS.ORANGE};
            margin-bottom: 20px;
          `}
        >
          Our Story
        </Uppercase>

        <TitleMedium
          css={css`
            margin-bottom: 60px;
          `}
        >
          Small Door began with a simple goal: to deliver superior veterinary
          care for pets while providing a personalized, modern, and convenient
          experience for pet parents.
        </TitleMedium>

        <div
          css={css`
            display: flex;
            justify-content: space-between;
            @media ${BREAKPOINTS.MOBILE} {
              flex-direction: column;
            }
          `}
        >
          <div
            css={css`
              display: inline-block;
              width: 48%;
              @media ${BREAKPOINTS.MOBILE} {
                width: 100%;
              }
            `}
          >
            <ParagraphLarge>
              The typical veterinary practice today is antiquated. One of our
              founders learned this first hand after his boxer, Morris, came
              down with a mystery stomach ailment. He visited eight vet clinics
              in search of a diagnosis, racking up thousands of dollars in bills
              as Morris withstood one test — and cheerless waiting room — after
              another. Same-day appointments were rarely available and medical
              records were closely guarded. What's more, he could never
              communicate directly with a doctor, by phone or otherwise.
            </ParagraphLarge>
            <ParagraphLarge>We're changing all of that.</ParagraphLarge>
            <ParagraphLarge>
              Our founders envisioned a different sort of veterinary experience
              that combines modern technology with warm hospitality and a
              commitment to transparency. And, most importantly, integrative and
              reliable care for pets like Morris, who, after finally receiving a
              diagnosis and treatment, returned to his happy life playing
              tug-of-war and bodysurfing ocean waves.
            </ParagraphLarge>
          </div>

          <div
            css={css`
              display: inline-block;
              width: 40%;
              @media ${BREAKPOINTS.MOBILE} {
                order: -1;
                width: 100%;
                margin-bottom: 40px;
              }
            `}
          >
            <Img fluid={data.dogInWater.childImageSharp.fluid} />
          </div>
        </div>
      </OurStory>

      <Img
        fluid={data.ladyLayingWithDog.childImageSharp.fluid}
        alt='Lady laying down with dog'
      />

      <PeaceOfMind>
        <Uppercase
          css={css`
            color: ${COLORS.ORANGE};
            margin-bottom: 20px;
          `}
        >
          Best of all — peace of mind
        </Uppercase>
        <POMPoints>
          <POMPoint>
            <strong>Transparency matters.</strong> Before we conduct a test or
            procedure, we ensure you understand the costs and your options: no
            hidden fees, no surprises. Our rates are fair and clearly
            communicated without a lot of “medicalese”.
          </POMPoint>
          <POMPoint>
            <strong>Our veterinary spaces are designed with empathy.</strong>{' '}
            We've consulted with renowned animal scientists to create
            comfortable, private, and stress-free environments for you and your
            pet.
          </POMPoint>
          <POMPoint>
            <strong>Your pet's well-being always comes first.</strong>{' '}
            Sometimes, that means referring you to a specialist, even if it's
            not in our financial interest.
          </POMPoint>
          <POMPoint>
            <strong>Your pet's data belongs to you.</strong> It's your right. We
            make it easier than ever to obtain and manage your health records.
          </POMPoint>
        </POMPoints>
      </PeaceOfMind>

      <OurResponsibilityWrapper>
        <OurResponsibility>
          <Uppercase
            css={css`
              color: ${COLORS.ORANGE};
              margin-bottom: 20px;
            `}
          >
            Our responsibility
          </Uppercase>

          <TitleMedium
            css={css`
              margin-bottom: 60px;
            `}
          >
            One of the reasons we started Small Door is because veterinarians
            are often denied the working conditions, benefits, and pay these
            heroes deserve.
          </TitleMedium>

          <div
            css={css`
              display: flex;
              justify-content: space-between;
              @media ${BREAKPOINTS.MOBILE} {
                flex-direction: column;
              }
            `}
          >
            <div
              css={css`
                display: inline-block;
                width: 48%;
                @media ${BREAKPOINTS.MOBILE} {
                  width: 100%;
                }
              `}
            >
              <ParagraphLarge>
                Our goal is to provide veterinarians with the support and
                resources they need to be their best. In fact, their well being
                is incorporated into our founding charter. With every decision
                we make, we take their well-being and success into account.
              </ParagraphLarge>
              <ParagraphLarge>
                Small Door is a Certified B Corporation, held to a rigorous
                standard of social and environmental performance, public
                transparency, and accountability.
              </ParagraphLarge>
              <ParagraphLarge>
                We are also a Public Benefit Corporation, which means we operate
                not only to maximize profits but to make the world a better
                place in concrete, measurable ways.
              </ParagraphLarge>
            </div>

            <div
              css={css`
                display: inline-block;
                width: 40%;
                @media ${BREAKPOINTS.MOBILE} {
                  width: 100%;
                  order: -1;
                  margin-bottom: 40px;
                }
              `}
            >
              <Img
                fluid={data.feetPaws.childImageSharp.fluid}
                alt='Human feet and dog paws under blanket'
              />
            </div>
          </div>
        </OurResponsibility>
      </OurResponsibilityWrapper>
    </Layout>
  );
};

export default AboutUsPage;
